import { useRef, useState } from "react";
import { Upload, Link as LinkIcon, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { UploadsAPI, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * ImageInput — reusable image picker for admin/coach forms.
 *
 * Props:
 *  - value: current URL (string)
 *  - onChange: (url) => void
 *  - placeholder: hint text for URL input
 *  - testId: data-testid prefix
 *  - aspect: "square" | "video" | "wide" (preview ratio)
 *  - size: "sm" | "md" | "lg"
 *
 * Behaviour:
 *  - Click the dropzone (or drag&drop) to upload an image from the device.
 *  - Or paste a URL in the text field below.
 *  - Live preview + remove button.
 */
export default function ImageInput({
  value = "",
  onChange,
  placeholder = "https://… ou télécharge une image",
  testId = "image",
  aspect = "square",
  size = "md",
  accept = "image/*",
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const display = resolveImageUrl(value);

  const sizeClasses = {
    sm: "h-20 w-20",
    md: "h-28 w-28",
    lg: "h-40 w-full max-w-xs",
  };
  const aspectClass =
    aspect === "video"
      ? "aspect-video w-full"
      : aspect === "wide"
        ? "aspect-[16/10] w-full"
        : sizeClasses[size];

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Sélectionne une image (JPG, PNG, WEBP, HEIC).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop lourde (max 8 MB).");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { url } = await UploadsAPI.image(file, setProgress);
      onChange?.(url);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (file) doUpload(file);
    e.target.value = ""; // allow re-pick same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div className="space-y-2" data-testid={`${testId}-root`}>
      <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
        {/* Dropzone / preview */}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid={`${testId}-dropzone`}
          className={`${aspectClass} relative rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0 transition-all bg-white/[0.03] hover:bg-white/[0.06]`}
          style={{
            borderColor: dragging
              ? "#D4AF37"
              : display
                ? "rgba(255,255,255,0.12)"
                : "rgba(255,255,255,0.18)",
          }}
        >
          {display ? (
            <img
              src={display}
              alt=""
              className="w-full h-full object-cover"
              data-testid={`${testId}-preview`}
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-zinc-500 px-2 text-center">
              <ImageIcon className="w-5 h-5" />
              <span className="text-[9px] uppercase tracking-[0.18em] font-bold">
                Choisir<br />une image
              </span>
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-1">
              <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
              <span className="text-[10px] font-bold text-white tabular-nums">
                {progress}%
              </span>
            </div>
          )}
        </button>

        {/* Side buttons */}
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testId}-upload-btn`}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold border border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white hover:bg-white/[0.08] rounded-md px-3 py-2 disabled:opacity-50"
          >
            <Upload className="w-3 h-3" /> Galerie
          </button>
          {display && (
            <button
              type="button"
              onClick={() => onChange?.("")}
              data-testid={`${testId}-clear`}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold border border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 rounded-md px-3 py-2"
            >
              <X className="w-3 h-3" /> Retirer
            </button>
          )}
        </div>
      </div>

      {/* URL fallback */}
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
        <LinkIcon className="w-3 h-3" /> ou colle une URL
      </div>
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        data-testid={`${testId}-url`}
        className="bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 text-xs focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37]"
      />

      <input
        ref={fileRef}
        type="file"
        accept={accept}
        onChange={onFile}
        className="hidden"
        data-testid={`${testId}-file-input`}
      />
    </div>
  );
}
